/**
 * Embed Product Page (layoutless)
 *
 * Renders a single product component for embedding in external iframes
 * or third-party pages. Shows only images, title, price, variant selector,
 * and add-to-cart — no breadcrumbs, reviews, recommendations, or footer.
 */

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { useProduct, useAddToCart } from '@/lib/hooks/use-product';
import { useCartContext } from '@/lib/context/cart-context';
import { ProductDetails } from '@/components/organisms/ProductDetails';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;
`;

const ErrorMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: #666;
`;

export function EmbedProductPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: product, isLoading, error } = useProduct(slug ?? '');
  const addToCartMutation = useAddToCart();
  const { cartToken, setCartToken } = useCartContext();
  const [selectedVariantId, setSelectedVariantId] = useState<string | undefined>();

  useEffect(() => {
    if (product) {
      document.title = product.title;
    }
  }, [product]);

  useEffect(() => {
    if (product && !selectedVariantId) {
      const defaultVariant = product.variants.find(v => v.enabled) || product.variants[0];
      if (defaultVariant) {
        setSelectedVariantId(defaultVariant.id);
      }
    }
  }, [product, selectedVariantId]);

  const handleAddToCart = async (variantId: string, quantity: number) => {
    if (!product) return;
    const result = await addToCartMutation.mutateAsync({
      cartToken: cartToken || undefined,
      productId: product.uuid,
      variantId,
      quantity,
      options: { countryCode: 'US', currency: 'USD', vendor: 'shopify', shop: 'shapermint' },
    });

    if (result.cartToken) {
      setCartToken(result.cartToken);
    }
  };

  if (!slug) {
    return <ErrorMessage>Invalid product URL</ErrorMessage>;
  }

  if (isLoading) {
    return <ErrorMessage>Loading...</ErrorMessage>;
  }

  if (error || !product) {
    return <ErrorMessage>Product not found</ErrorMessage>;
  }

  return (
    <Container>
      <ProductDetails
        product={product}
        selectedVariantId={selectedVariantId}
        onVariantChange={setSelectedVariantId}
        onAddToCart={handleAddToCart}
      />
    </Container>
  );
}
